import {
  ActivitySquare,
  AlertCircle,
  ClipboardCheck,
  Clock,
  Home,
  Mail,
  MapPin,
  Phone,
  User,
} from "@tamagui/lucide-icons";
import {
  Customer,
  SensorAlert,
  SensorAlertType,
  SensorType,
  ServiceCompany,
  useSensorAlert,
  Hub,
} from "common";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router";
import {
  Anchor,
  Circle,
  Heading,
  Image,
  Separator,
  Spinner,
  Text,
  View,
  XStack,
  YStack,
  styled,
} from "tamagui";
import { GradientButton, OutlinedButton, tokens } from "tamagui-config";

import { humidityAlertIcon } from "../../assets/icons";
import { ActionMenu } from "../../components/ActionMenu";
import { GradientCard } from "../../components/GradientCard";
import { KPICard } from "../../components/KPICard";
import { StatusPill } from "../../components/StatusPill";
import { useSensorType } from "../../hooks/useSensorType";
import { useStatusPillProps } from "../../hooks/useStatusPillProps";
import { parsePhoneNumber } from "../../utils/parsePhoneNumber";

const MAP_HEIGHT = 300;
const MAP_BOUNDING_BOX_COORDINATE_OFFSET = 0.001; // This is roughly the map zoom level (the smaller, the more zoomed in)

// Equal-width tiles in a wrapping row. The four summary tiles go 2x2 below
// `lg` and stack on a phone; the three-across info tiles stack below `sm`.
const SummarySlot = styled(View, {
  flexGrow: 1,
  flexBasis: 0,
  $lg: { flexBasis: "40%" },
  $xs: { flexBasis: "100%" },
});
const InfoSlot = styled(View, {
  flexGrow: 1,
  flexBasis: 0,
  $sm: { flexBasis: "100%" },
});

export const AlertDetails = () => {
  const { id } = useParams();
  const {
    sensorAlert,
    isFetching,
    updateSensorAlertAcknowledgedState,
    isUpdating,
  } = useSensorAlert(id ?? "");

  const { translateSensorType } = useSensorType();
  const { generateAlertStatusPillProps } = useStatusPillProps();
  const statusPillProps = generateAlertStatusPillProps(sensorAlert);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const date = sensorAlert?.timestamp
    ? new Date(sensorAlert.timestamp * 1000)
    : new Date();

  const onToggleAcknowledgedStatePress = async () => {
    if (!sensorAlert) {
      return;
    }

    await updateSensorAlertAcknowledgedState({
      id: sensorAlert.id,
      isAcknowledged: !sensorAlert.acknowledgedAt,
    });
  };

  if (!sensorAlert || isFetching) {
    return (
      <YStack flex={1} gap="$2" justifyContent="center" alignItems="center">
        <Spinner size="large" />
        <Text>{t("common.loading")}</Text>
      </YStack>
    );
  }

  return (
    <YStack gap="$5">
      <YStack gap="$2">
        <Text fontSize="$6">{t("alerts.title")}</Text>
        <XStack
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap="$3"
        >
          <Heading
            size="$14"
            fontSize="$14"
            $xs={{ fontSize: "$12", lineHeight: "$12" }}
          >
            {t("alertDetails.title")}
          </Heading>
          <XStack gap="$3.5" alignItems="center">
            <ActionMenu placement="bottom-end">
              <ActionMenu.Trigger>
                {({ chevronIcon }) => (
                  <GradientButton elevated rounded iconAfter={chevronIcon}>
                    {t("alertDetails.actionButton")}
                  </GradientButton>
                )}
              </ActionMenu.Trigger>
              <ActionMenu.Menu>
                {!sensorAlert.serviceRequest ? (
                  <ActionMenu.MenuItem
                    onPress={() =>
                      navigate("/service-requests/new", {
                        state: {
                          customerId: sensorAlert.customer?.id,
                          serviceCompanyId: sensorAlert.serviceCompany?.id,
                          alertId: sensorAlert.id,
                          hubId: sensorAlert.hub?.id,
                          sensorId: sensorAlert.sensor?.id,
                        },
                      })
                    }
                    hoverStyle={{ backgroundColor: "$white7" }}
                    pressStyle={{ backgroundColor: "$white7", scale: 0.97 }}
                  >
                    {t("alertDetails.actionMenu.createServiceRequest")}
                  </ActionMenu.MenuItem>
                ) : (
                  <ActionMenu.MenuItem
                    onPress={() =>
                      navigate(
                        `/service-requests/${sensorAlert.serviceRequest?.id}`,
                      )
                    }
                    hoverStyle={{ backgroundColor: "$white7" }}
                    pressStyle={{ backgroundColor: "$white7", scale: 0.97 }}
                  >
                    {t("alertDetails.actionMenu.goToServiceRequest")}
                  </ActionMenu.MenuItem>
                )}
              </ActionMenu.Menu>
            </ActionMenu>

            <OutlinedButton
              rounded
              onPress={onToggleAcknowledgedStatePress}
              loading={isUpdating}
            >
              {sensorAlert.acknowledgedAt
                ? t("alertDetails.reopenButton")
                : t("alertDetails.clearButton")}
            </OutlinedButton>
          </XStack>
        </XStack>
      </YStack>

      <Separator />

      <XStack gap="$3.5" flexWrap="wrap">
        <SummarySlot>
          <KPICard
            icon={<AlertCircle size="$1.5" />}
            iconCircleSize={36}
            title={
              <Text fontWeight="bold">
                {t("alertDetails.summary.sensorType")}
              </Text>
            }
            description={translateSensorType(
              sensorAlert.sensor?.type as SensorType | undefined,
            )}
          />
        </SummarySlot>
        <SummarySlot>
          <KPICard
            icon={<AlertCircle size="$1.5" />}
            iconCircleSize={40}
            title={
              <Text fontWeight="bold">{t("alertDetails.summary.alert")}</Text>
            }
            description={sensorAlert.message}
          />
        </SummarySlot>
        <SummarySlot>
          <KPICard
            icon={<Clock size="$1.5" />}
            iconCircleSize={40}
            title={
              <Text fontWeight="bold">{t("alertDetails.summary.date")}</Text>
            }
            description={t("alertDetails.summary.dateValue", {
              date,
            })}
          />
        </SummarySlot>
        <SummarySlot>
          <KPICard
            icon={<ActivitySquare size="$1.5" />}
            iconCircleSize={40}
            title={
              <Text fontWeight="bold">
                {t("alertDetails.summary.severity")}
              </Text>
            }
            description={<StatusPill fontSize="$2" {...statusPillProps} />}
          />
        </SummarySlot>
      </XStack>

      {sensorAlert.customer && (
        <CustomerDetailsCard customer={sensorAlert.customer} />
      )}
      <AlertDetailsCard sensorAlert={sensorAlert} />

      {sensorAlert.hub && (
        <DirectionsCard
          hub={sensorAlert.hub}
          serviceCompany={sensorAlert.serviceCompany}
        />
      )}
    </YStack>
  );
};

const AlertDetailsCard = ({ sensorAlert }: { sensorAlert: SensorAlert }) => {
  const { t } = useTranslation();
  const { generateAlertStatusPillProps } = useStatusPillProps();
  const statusPillProps = generateAlertStatusPillProps(sensorAlert);

  const getAlertDescription = (): string => {
    if (
      sensorAlert.alertType === SensorAlertType.HighHumidity &&
      sensorAlert.value
    ) {
      const humidity = sensorAlert.value;

      switch (sensorAlert.severity) {
        case "medium":
          return `Slightly Humid (${humidity}%)`;
        case "high":
          return `High Humidity (${humidity}%)`;
        case "critical":
          return `Extremely Humid (${humidity}%)`;
        default:
          return `Humid (${humidity}%)`;
      }
    }

    return sensorAlert.message || t("alertDetails.alert.defaultMessage");
  };
  const getAlertIcon = () => {
    if (sensorAlert.alertType === SensorAlertType.HighHumidity) {
      return (
        <Image
          source={{ uri: humidityAlertIcon }}
          width={24}
          height={24}
          tintColor={tokens.color.white}
          alt={t("alertDetails.alert.humidityAlertIcon")}
        />
      );
    }

    return <AlertCircle size="$1.5" />;
  };

  const getSensorLocation = (): string => {
    // Hub-level alerts carry the hub id as sensorId — the base station is the subject.
    const isHubAlert =
      sensorAlert.alertType?.startsWith("HUB_") ||
      sensorAlert.sensorId === sensorAlert.hubId;
    if (isHubAlert) {
      return t("alertDetails.alert.baseStation", {
        hub: sensorAlert.hub?.name ?? "",
      });
    }
    const location = sensorAlert.sensor?.location;
    const sublocation = sensorAlert.sensor?.sublocation;

    if (location && sublocation && location !== sublocation) {
      return `${location} - ${sublocation}`;
    }

    if (location) {
      return location;
    }

    if (sublocation) {
      return sublocation;
    }

    return t("alertDetails.alert.unknownLocation");
  };

  return (
    <GradientCard backgroundColor="$transparentBorder" backgroundImage="none">
      <Text fontWeight="bold">{t("alertDetails.alert.title")}</Text>
      <XStack gap="$3.5" flexWrap="wrap">
        <InfoSlot>
          <XStack
            gap="$3.5"
            alignItems="center"
            padding="$3.5"
            backgroundColor="$gray1"
            borderRadius="$4"
          >
            <Circle size={40} backgroundColor="$transparentBorder">
              {getAlertIcon()}
            </Circle>
            <YStack flex={1} gap="$1.5">
              <Text fontWeight="bold">
                {t("alertDetails.alert.description")}
              </Text>
              <Text
                color="$white10"
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                $sm={{ whiteSpace: "normal" }}
              >
                {getAlertDescription()}
              </Text>
            </YStack>
          </XStack>
        </InfoSlot>

        <InfoSlot>
          <XStack
            gap="$3.5"
            alignItems="center"
            padding="$3.5"
            backgroundColor="$gray1"
            borderRadius="$4"
          >
            <Circle size={40} backgroundColor="$transparentBorder">
              <Home size="$1.5" />
            </Circle>
            <YStack flex={1} gap="$1.5">
              <Text fontWeight="bold">
                {t("alertDetails.alert.sensorLocation")}
              </Text>
              <Text
                color="$white10"
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                $sm={{ whiteSpace: "normal" }}
              >
                {getSensorLocation()}
              </Text>
            </YStack>
          </XStack>
        </InfoSlot>

        <InfoSlot>
          <XStack
            gap="$3.5"
            alignItems="center"
            padding="$3.5"
            backgroundColor="$gray1"
            borderRadius="$4"
          >
            <Circle size={40} backgroundColor="$transparentBorder">
              <ClipboardCheck size="$1.5" />
            </Circle>
            <YStack flex={1} gap="$1.5">
              <Text fontWeight="bold">{t("alertDetails.alert.severity")}</Text>
              <StatusPill fontSize="$2" {...statusPillProps} />
            </YStack>
          </XStack>
        </InfoSlot>
      </XStack>
    </GradientCard>
  );
};

const CustomerDetailsCard = ({ customer }: { customer: Customer }) => {
  const { t } = useTranslation();

  return (
    <GradientCard backgroundColor="$transparentBorder" backgroundImage="none">
      <Text fontWeight="bold">{t("alertDetails.customer.title")}</Text>
      <XStack gap="$3.5" flexWrap="wrap">
        <InfoSlot>
          <XStack
            gap="$3.5"
            alignItems="center"
            padding="$3.5"
            backgroundColor="$gray1"
            borderRadius="$4"
          >
            <Circle size={40} backgroundColor="$transparentBorder">
              <User size="$1.5" />
            </Circle>
            <YStack flex={1} gap="$1.5">
              <Text fontWeight="bold">{t("alertDetails.customer.name")}</Text>
              {customer.name ? (
                <Text
                  color="$white10"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                >
                  {customer.name}
                </Text>
              ) : (
                <Text color="$white10">
                  {t("alertDetails.customer.nameNotProvided")}
                </Text>
              )}
            </YStack>
          </XStack>
        </InfoSlot>

        <InfoSlot>
          <XStack
            gap="$3.5"
            alignItems="center"
            padding="$3.5"
            backgroundColor="$gray1"
            borderRadius="$4"
          >
            <Circle size={40} backgroundColor="$transparentBorder">
              <MapPin size="$1.5" />
            </Circle>
            <YStack flex={1} gap="$1.5">
              <Text fontWeight="bold">
                {t("alertDetails.customer.address")}
              </Text>
              {customer.city ? (
                <Text
                  color="$white10"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  $sm={{ whiteSpace: "normal" }}
                >
                  {t("alertDetails.customer.addressValue", {
                    street: customer.address,
                    city: customer.city,
                    state: customer.state,
                    zip: customer.zipCode,
                  })}
                </Text>
              ) : (
                <Text color="$white10">
                  {t("alertDetails.customer.addressNotProvided")}
                </Text>
              )}
            </YStack>
          </XStack>
        </InfoSlot>

        <InfoSlot>
          <XStack
            gap="$3.5"
            alignItems="center"
            padding="$3.5"
            backgroundColor="$gray1"
            borderRadius="$4"
          >
            <Circle size={40} backgroundColor="$transparentBorder">
              <Phone size="$1.5" />
            </Circle>
            <YStack flex={1} gap="$1.5">
              <Text fontWeight="bold">{t("alertDetails.customer.phone")}</Text>
              {customer.phone ? (
                <Text
                  color="$white10"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                >
                  {t("alertDetails.customer.phoneValue", {
                    phone: customer.phone,
                  })}
                </Text>
              ) : (
                <Text color="$white10">
                  {t("alertDetails.customer.phoneNotProvided")}
                </Text>
              )}
            </YStack>
          </XStack>
        </InfoSlot>
      </XStack>
    </GradientCard>
  );
};

const DirectionsCard = ({
  hub,
  serviceCompany,
}: {
  hub: Hub;
  serviceCompany?: ServiceCompany;
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapContainerWidth, setMapContainerWidth] = useState<number | null>(
    null,
  );
  const { t } = useTranslation();

  const mapImageUri = useMemo(() => {
    const { latitude, longitude } = hub;

    if (!latitude || !longitude || !mapContainerWidth) {
      return null;
    }

    const boundingBox = [
      longitude - MAP_BOUNDING_BOX_COORDINATE_OFFSET, // Min longitude
      latitude - MAP_BOUNDING_BOX_COORDINATE_OFFSET, // Min latitude
      longitude + MAP_BOUNDING_BOX_COORDINATE_OFFSET, // Max longitude
      latitude + MAP_BOUNDING_BOX_COORDINATE_OFFSET, // Max latitude
    ].join(",");

    const width = Math.floor(mapContainerWidth);
    const height = MAP_HEIGHT;

    return (
      "https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/" +
      `[${boundingBox}]/` +
      `${width}x${height}@2x?` +
      `padding=0,0,0,0&access_token=${process.env.MAPBOX_TOKEN}&logo=false`
    );
  }, [hub.latitude, hub.longitude, mapContainerWidth]);

  useEffect(() => {
    if (mapContainerRef.current && !mapContainerWidth) {
      setMapContainerWidth(mapContainerRef.current.offsetWidth);
    }
  }, [mapContainerWidth]);

  return (
    <GradientCard backgroundColor="$transparentBorder" backgroundImage="none">
      <Text fontWeight="bold">{t("alertDetails.location.title")}</Text>

      <View
        ref={mapContainerRef}
        width="100%"
        height={MAP_HEIGHT}
        position="relative"
        alignItems="center"
        justifyContent="center"
        borderWidth={2}
        borderColor="$transparentBorder"
        borderRadius="$4"
        overflow="hidden"
      >
        {mapImageUri && (
          <>
            <Image
              source={{ uri: mapImageUri }}
              position="absolute"
              width="100%"
              height={MAP_HEIGHT}
              resizeMode="cover"
              alt={t("alertDetails.location.mapAlt")}
            />
            <MapPin size={40} color="$hgPrimary" zIndex={1} />
          </>
        )}
      </View>

      {serviceCompany && (
        <>
          <XStack gap="$3.5" alignItems="center" flexWrap="wrap">
            {serviceCompany.logoUrl && (
              <Image
                source={{ uri: serviceCompany.logoUrl }}
                width={147}
                height={56}
                resizeMode="stretch"
                alt={t("sidebar.homeServiceLogoAlt")}
                alignSelf="center"
              />
            )}
            <Text fontSize="$6" fontWeight="bold">
              {serviceCompany.name}
            </Text>
          </XStack>

          <XStack gap="$3.5" flexWrap="wrap">
            <InfoSlot>
              <XStack
                gap="$2"
                alignItems="center"
                padding="$3.5"
                backgroundColor="$gray1"
                borderRadius="$4"
              >
                <Circle size={40} backgroundColor="$transparentBorder">
                  <MapPin size="$1" />
                </Circle>
                <YStack flex={1} gap="$1.5">
                  {serviceCompany.city ? (
                    <Text>
                      {t("alertDetails.location.addressValue", {
                        street: serviceCompany.address ?? "",
                        city: serviceCompany.city ?? "",
                        state: serviceCompany.state ?? "",
                        zip: serviceCompany.zipCode ?? "",
                      })}
                    </Text>
                  ) : (
                    <Text color="$white10">
                      {t("alertDetails.location.addressNotProvided")}
                    </Text>
                  )}
                </YStack>
              </XStack>
            </InfoSlot>

            <InfoSlot>
              <XStack
                gap="$2"
                alignItems="center"
                padding="$3.5"
                backgroundColor="$gray1"
                borderRadius="$4"
              >
                <Circle size={36} backgroundColor="$transparentBorder">
                  <Phone size="$1" />
                </Circle>
                <YStack flex={1} gap="$1.5">
                  {serviceCompany.phone ? (
                    <Anchor
                      href={
                        parsePhoneNumber(serviceCompany.phone)?.getURI() ??
                        `tel:${serviceCompany.phone}`
                      }
                      fontWeight="bold"
                      color="$hgPrimary"
                      textDecorationLine="none"
                    >
                      {t("alertDetails.location.phoneValue", {
                        phone: serviceCompany.phone,
                      })}
                    </Anchor>
                  ) : (
                    <Text color="$white10">
                      {t("alertDetails.location.phoneNotProvided")}
                    </Text>
                  )}
                </YStack>
              </XStack>
            </InfoSlot>

            <InfoSlot>
              <XStack
                gap="$2"
                alignItems="center"
                padding="$3.5"
                backgroundColor="$gray1"
                borderRadius="$4"
              >
                <Circle size={36} backgroundColor="$transparentBorder">
                  <Mail size="$1" />
                </Circle>
                <YStack flex={1} gap="$1.5">
                  {serviceCompany.email ? (
                    <Anchor
                      href={`mailto:${serviceCompany.email}`}
                      fontWeight="bold"
                      color="$hgPrimary"
                      textDecorationLine="none"
                      overflow="hidden"
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                    >
                      {serviceCompany.email}
                    </Anchor>
                  ) : (
                    <Text color="$white10">
                      {t("alertDetails.location.emailNotProvided")}
                    </Text>
                  )}
                </YStack>
              </XStack>
            </InfoSlot>
          </XStack>
        </>
      )}
    </GradientCard>
  );
};

export default AlertDetails;
